/**
 * Approach cards shown inline in the chat flow.
 * Stacked vertically with icon on the left (horizontal layout within each card).
 * Spring-based staggered entrance with hover/tap micro-interactions.
 */

import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FlaskConical, Lightbulb, MessageSquare, ChevronDown } from 'lucide-react';
import { cardContainerVariants, chipVariants } from './animations';

interface Props {
  onSelect: (intent: 'score_driven' | 'targeted_revision') => void;
  onDirectRun?: () => void;
  disabled?: boolean;
}

const ApproachSelector: React.FC<Props> = ({ onSelect, onDirectRun, disabled }) => {
  const [howItWorksOpen, setHowItWorksOpen] = useState(false);

  return (
    <motion.div
      variants={cardContainerVariants}
      initial="initial"
      animate="animate"
      className="flex flex-col gap-3 w-full"
    >
      <motion.button
        variants={chipVariants}
        whileHover={{ scale: 1.015 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => !disabled && onSelect('score_driven')}
        disabled={disabled}
        className="group flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 text-left transition-colors hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:pointer-events-none"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-all duration-200 group-hover:bg-primary/10 group-hover:text-primary">
          <Lightbulb className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-heading text-sm">Review Insights</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            See how your agent scored in recent sessions and get targeted recommendations.
          </p>
        </div>
      </motion.button>

      <motion.button
        variants={chipVariants}
        whileHover={{ scale: 1.015 }}
        whileTap={{ scale: 0.98 }}
        onClick={() => !disabled && onSelect('targeted_revision')}
        disabled={disabled}
        className="group flex items-center gap-4 rounded-xl border border-border/60 bg-card p-4 text-left transition-colors hover:border-primary/30 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50 disabled:pointer-events-none"
      >
        <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-muted/60 text-muted-foreground transition-all duration-200 group-hover:bg-primary/10 group-hover:text-primary">
          <MessageSquare className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="font-medium text-heading text-sm">Share Your Feedback</h3>
          <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
            Tell us what to change — we'll generate updated prompts based on your input.
          </p>
        </div>
      </motion.button>

      {onDirectRun && (
        <motion.button
          variants={chipVariants}
          whileHover={{ scale: 1.015 }}
          whileTap={{ scale: 0.98 }}
          onClick={() => !disabled && onDirectRun()}
          disabled={disabled}
          className="group flex items-center gap-4 rounded-xl border border-primary/20 bg-primary/5 p-4 text-left transition-colors hover:border-primary/40 hover:bg-primary/10 hover:shadow-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none disabled:opacity-50"
        >
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary transition-all duration-200 group-hover:bg-primary/15">
            <FlaskConical className="h-5 w-5" />
          </div>
          <div className="min-w-0">
            <h3 className="font-medium text-heading text-sm">Simulate & Score</h3>
            <p className="text-xs text-muted-foreground mt-0.5 leading-relaxed">
              Run simulated conversations against your current agent and benchmark the results.
            </p>
          </div>
        </motion.button>
      )}

      {/* How it works — expandable onboarding */}
      <motion.div variants={chipVariants} className="pt-1">
        <button
          type="button"
          onClick={() => setHowItWorksOpen((prev) => !prev)}
          className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-body transition-colors"
        >
          <ChevronDown
            className={`h-3.5 w-3.5 transition-transform duration-200 ${howItWorksOpen ? 'rotate-180' : ''}`}
          />
          How does this work?
        </button>
        <AnimatePresence>
          {howItWorksOpen && (
            <motion.div
              initial={{ height: 0, opacity: 0 }}
              animate={{ height: 'auto', opacity: 1 }}
              exit={{ height: 0, opacity: 0 }}
              transition={{ duration: 0.2 }}
              className="overflow-hidden"
            >
              <div className="mt-2 rounded-lg bg-muted/30 border border-border/40 p-3 space-y-2 text-xs text-muted-foreground leading-relaxed">
                <p>
                  <span className="font-medium text-body">Review Insights</span> analyzes your agent&apos;s recent conversations, identifies where it underperformed, and generates targeted prompt changes to improve those areas.
                </p>
                <p>
                  <span className="font-medium text-body">Share Your Feedback</span> lets you describe what you want changed in your own words. We&apos;ll ask a few clarifying questions, then generate the exact prompt edits.
                </p>
                <p>
                  Either way, you&apos;ll review all changes before anything is saved — and you can always refine further.
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>
    </motion.div>
  );
};

export default ApproachSelector;
